import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Loader2, Plus, KeyRound, Eye, EyeOff } from "lucide-react";
import { AdminFilterBar } from "@/components/admin/AdminFilterBar";

const ROLE_OPTIONS = [
  "SUPER_ADMIN",
  "PLAYER_ADMIN",
  "PROVIDER_ADMIN",
  "COACH_ADMIN",
  "FINANCE_ADMIN",
  "PAYMENT_REMITTANCE_MANAGER",
  "FUND_MANAGER",
  "COMMUNICATIONS_MANAGER",
  "SUPPORT_MANAGER",
  "COORDINATOR_MANAGER",
  "COORDINATOR",
];

export default function StaffUsers() {
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", staff_department: "Back Office", backoffice_roles: ["PLAYER_ADMIN"], checker_group: "", maker_checker_mode: "pool" });

  // Reset Password Dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchStaff = async () => {
    try {
      const res = await api.get("/admin/staff-users");
      setStaffUsers(res.data.staff_users || []);
    } catch {
      toast.error("Failed to load staff users");
      setStaffUsers([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchStaff(); }, []);

  const filteredStaff = staffUsers.filter((user) => {
    const matchesSearch = !query || [user.name, user.email, user.staff_department].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase());
    const matchesStatus = statusFilter === "all" || user.staff_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const createStaffUser = async () => {
    try {
      const res = await api.post("/admin/staff-users", form);
      if (res.data.request_submitted) {
        toast.success(`Approval request submitted: ${res.data.approval_request.request_id}`);
      }
      setOpen(false);
      setForm({ name: "", email: "", phone: "", staff_department: "Back Office", backoffice_roles: ["PLAYER_ADMIN"], checker_group: "", maker_checker_mode: "pool" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create staff user");
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResetting(true);
    try {
      await api.post("/admin/set-user-password", { email: resetTarget.email, password: newPassword });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetOpen(false);
      setResetTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    }
    setResetting(false);
  };

  const openResetDialog = (user) => {
    setResetTarget(user);
    setNewPassword("");
    setShowPassword(false);
    setResetOpen(true);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="staff-users-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3"><Users className="w-8 h-8 text-court-orange" />Staff Users</h1>
          <p className="text-muted-foreground mt-1">Create and manage back-office user accounts and staff roles.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-md" data-testid="create-staff-user-btn"><Plus className="w-4 h-4 mr-2" />Create Staff User</Button>
      </div>

      <AdminFilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search staff users by name, email, or department"
        searchTestId="staff-user-search-input"
        filters={[
          { key: "status", label: "Status", value: statusFilter, onChange: setStatusFilter, options: [{ value: "all", label: "All Statuses" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "suspended", label: "Suspended" }], testId: "staff-user-status-filter" },
        ]}
        activeFilters={[
          query ? { key: "search", label: `Search: ${query}`, onRemove: () => setQuery("") } : null,
          statusFilter !== "all" ? { key: "status", label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("all") } : null,
        ].filter(Boolean)}
        onClearAll={() => { setQuery(""); setStatusFilter("all"); }}
      />

      <div className="grid grid-cols-1 gap-3">
        {filteredStaff.map((user) => (
          <Card key={user.user_id} data-testid={`staff-user-${user.user_id}`}>
            <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p className="font-medium text-zinc-900">{user.name}</p>
                <p className="text-xs text-zinc-500">{user.email} · {user.staff_department || 'No department'}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(user.backoffice_roles || []).map((role) => <Badge key={role} variant="outline">{role}</Badge>)}
                <Badge className={user.staff_status === "active" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>{user.staff_status}</Badge>
                <Button size="sm" variant="outline" onClick={() => openResetDialog(user)} className="h-7 text-xs ml-1" data-testid={`reset-password-${user.user_id}`}>
                  <KeyRound className="w-3 h-3 mr-1" />Reset Password
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Staff Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-staff-user-dialog">
          <DialogHeader>
            <DialogTitle>Create Staff User</DialogTitle>
            <DialogDescription className="sr-only">Fill in details to create a new staff user</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-user-name" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-user-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-user-phone" /></div>
            <div><Label>Department</Label><Input value={form.staff_department} onChange={(e) => setForm({ ...form, staff_department: e.target.value })} data-testid="staff-user-department" /></div>
            <div><Label>Primary Role</Label>
              <Select value={form.backoffice_roles[0]} onValueChange={(value) => setForm({ ...form, backoffice_roles: [value] })}>
                <SelectTrigger data-testid="staff-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Checker Group</Label><Input value={form.checker_group} onChange={(e) => setForm({ ...form, checker_group: e.target.value })} data-testid="staff-user-checker-group" /></div>
            <Button onClick={createStaffUser} className="w-full rounded-md" data-testid="submit-staff-user-btn">Create Staff User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" />Reset Staff Password</DialogTitle>
            <DialogDescription className="sr-only">Set a new password for this staff user</DialogDescription>
          </DialogHeader>
          {resetTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-sm">{resetTarget.name}</p>
                <p className="text-xs text-muted-foreground">{resetTarget.email}</p>
              </div>
              <div>
                <Label className="text-xs">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    className="pr-10"
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {newPassword && newPassword.length < 6 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleResetPassword} disabled={resetting || newPassword.length < 6} className="flex-1" data-testid="confirm-reset-password">
                  {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4 mr-2" />Reset Password</>}
                </Button>
                <Button variant="outline" onClick={() => setResetOpen(false)} className="flex-1">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
